import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useConsent } from '../../context';
import { initPosthog, setConsentSource } from '../../analytics/posthog';
import { identifyUser } from '../../analytics/track';
import { forgetFirstTouch, sendFirstTouch } from '../../analytics/attribution';
import { useAuthStore } from '../../stores/authStore';
import { updateAdsConsent } from '../../utils/analytics';
import { regionState } from '../../utils/blockedRegion';

export function AnalyticsGate() {
  const { analyticsConsent, consentSource } = useConsent();
  const initializedRef = useRef(false);

  // Google Ads waits in "denied" (index.html) until the bar is answered
  useEffect(() => {
    if (analyticsConsent === 'granted' || analyticsConsent === 'denied') {
      updateAdsConsent(analyticsConsent === 'granted');
    }
  }, [analyticsConsent]);

  useEffect(() => {
    if (analyticsConsent !== 'granted') return;
    // On every event, the first pageview included: whether analytics started by
    // default (outside the EU/EEA, UK and Switzerland, since 2026-09-30) or because
    // the bar was accepted, so the accepting half stays comparable with before
    setConsentSource(consentSource);

    if (initializedRef.current) {
      posthog.opt_in_capturing();
      return undefined;
    }

    // Not where Cloudflare blocks the API (utils/blockedRegion.js): every event and
    // recording from there was refused at the edge, 67k 403s a day (2026-10-16)
    let live = true;
    regionState().then((state) => {
      if (!live || state === 'blocked' || initializedRef.current) return;
      initPosthog();
      initializedRef.current = true;
      // Accepted after signing in: the session belongs to this account too
      identifyUser(useAuthStore.getState().user);
      // Now that they have said yes, where they came from can be sent
      sendFirstTouch(posthog);
    });
    return () => {
      live = false;
    };
  }, [analyticsConsent, consentSource]);

  useEffect(() => {
    if (analyticsConsent !== 'denied') return;

    // Declined: what the first load remembered is deleted, not held — and this
    // runs whether or not analytics was ever started, because the common case
    // is someone who says no the first time they are asked
    forgetFirstTouch();

    if (!initializedRef.current) return;
    posthog.opt_out_capturing();
    posthog.reset();
    initializedRef.current = false;
  }, [analyticsConsent]);

  // Vercel Speed Insights used to be drawn here; every report counted against the free
  // plan's monthly requests (2026-10-06), and PostHog keeps the web vitals
  return null;
}


